// EWM-243 — GA4 dataLayer push wrapper for Search analytics.
//
// Per Luke (2026-05-28): analytics platform is GA4 via GTM (existing GA4
// property reused; new containers AU `GTM-KR2Z9QMH`, NZ `GTM-P6DSRFT9`).
// The GTM head-snippet install lives in a separate ticket (EWM-269) —
// this module only *produces* events on `window.dataLayer`; the GTM
// container (once installed) consumes them and forwards to GA4.
//
// Three guarantees:
//   1. **Consent-gated.** Reuses `hasAnalyticsConsent()` (EWM-243a) so a
//      user who declined cookies fires no events. Same gate Algolia
//      Insights already uses — privacy rule lives in one place.
//   2. **Non-blocking.** Every push runs in a `queueMicrotask` so the
//      user-visible click/render is never blocked by analytics work.
//   3. **SSR-safe.** No-ops when `window` / `document` are undefined.
//
// Every event is typed so call-sites can't drift from the AC.

import { formatCentAmount } from '@/lib/utils/format-price';
import { normalizeBrandName } from '@/utils/mappers/normalize-brand-name';
import { hasAnalyticsConsent } from './consent';
import { hashSHA256 } from './hash';
import type { PageType } from './pageType';
import type { LineItem } from '@shared/types/cart/LineItem';

// ---------- internals ----------

interface DataLayerWindow {
  dataLayer?: Record<string, unknown>[];
}

const ensureDataLayer = (): Record<string, unknown>[] | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as DataLayerWindow;
  w.dataLayer = w.dataLayer ?? [];
  return w.dataLayer;
};

const pushEvent = (payload: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;
  if (!hasAnalyticsConsent()) return;
  // Defer one microtask so the event fires AFTER the user-visible effect
  // (AC: events never block the UI thread for the navigation/click).
  queueMicrotask(() => {
    const layer = ensureDataLayer();
    if (layer) layer.push(payload);
  });
};

// ---------- public event types ----------

export type SearchUserType = 'guest' | 'authed' | 'view_only';

/** Active refinements summarised for analytics — keys are the FE refinement IDs. */
export type SearchFilters = Record<string, string[] | boolean | string | null | undefined>;

interface BaseSearchPayload {
  query: string;
  user_type: SearchUserType;
  /**
   * commercetools account key when authenticated. Sent raw for parity
   * with the existing Algolia Insights `setAuthenticatedUserToken`
   * pattern. The AC marks hashing as a recommendation rather than a
   * hard requirement; swap to a hashed value if GA4 privacy posture
   * tightens.
   */
  customer_account_id?: string;
}

export interface SearchResultsViewedArgs extends BaseSearchPayload {
  filters: SearchFilters;
  sort: string;
  results_count: number;
  is_zero_results: boolean;
}

export interface SearchNoResultsArgs extends BaseSearchPayload {
  filters: SearchFilters;
}

export interface SearchRefinedArgs extends BaseSearchPayload {
  refinement_type: 'filter' | 'sort';
  refinement_value: string;
  /**
   * Result count *after* the refinement is applied. Undefined at the
   * moment of refinement because the SSR re-run hasn't returned yet;
   * the immediately-following `search_results_viewed` event carries
   * the authoritative count — GA4 can join the two by session+time.
   */
  results_count_after?: number;
}

export interface SearchResultClickArgs extends BaseSearchPayload {
  /** 1-based position across all loaded pages. */
  result_position: number;
  sku: string;
  product_name: string;
  /** Algolia hit objectID — paired with the Insights click event. */
  objectID: string;
}

export interface SearchResultAddToCartArgs extends BaseSearchPayload {
  sku: string;
  result_position: number;
  quantity: number;
}

// ---------- public push helpers ----------

export const pushSearchResultsViewed = (args: SearchResultsViewedArgs): void =>
  pushEvent({ event: 'search_results_viewed', ...args });

export const pushSearchNoResults = (args: SearchNoResultsArgs): void =>
  pushEvent({ event: 'search_no_results', ...args });

export const pushSearchRefined = (args: SearchRefinedArgs): void => pushEvent({ event: 'search_refined', ...args });

export const pushSearchResultClick = (args: SearchResultClickArgs): void =>
  pushEvent({ event: 'search_result_click', ...args });

export const pushSearchResultAddToCart = (args: SearchResultAddToCartArgs): void =>
  pushEvent({ event: 'search_result_add_to_cart', ...args });

// ---------- site & identity event types (EWM-368) ----------

export type UserStatus = 'guest' | 'authed' | 'view_only';
export type CountryCode = 'au' | 'nz';

export interface PageLoadArgs {
  page_type: PageType;
  user_status: UserStatus;
  customer_group: string;
  /** CT Business Unit key — present for authenticated users only. */
  business_unit?: string;
  /** 'active' | 'inactive' derived from account.isActive — present for authenticated users only. */
  account_status?: string;
  /** Postcode from default shipping address — only when the user has set one. */
  delivery_postcode?: string;
}

export interface CartItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_category?: string;
  item_brand?: string;
}

export interface CartContentsPushArgs {
  cart_total: number;
  cart_num_items: number;
  cart_items: CartItem[];
  latest_cart_item: CartItem[];
}

export interface UserIdentifyArgs {
  email: string;
  /** SHA-256 of normalised (lowercase, trimmed) email. */
  em: string;
  /** SHA-256 of normalised first name — omit key entirely when unavailable. */
  fn?: string;
  /** SHA-256 of normalised last name — omit key entirely when unavailable. */
  ln?: string;
  /** SHA-256 of normalised phone — omit key entirely when unavailable. */
  ph?: string;
  country_code: CountryCode;
  /** commercetools customer id — Braze external_id for changeUser (EWM-583). */
  customer_id?: string;
}

// ---------- site & identity push helpers (EWM-368) ----------

export const pushPageLoad = (args: PageLoadArgs): void => pushEvent({ event: 'page_load', ...args });

export const pushCartContentsPush = (args: CartContentsPushArgs): void =>
  pushEvent({ event: 'cart_contents_push', ...args });

export const pushUserIdentify = (args: UserIdentifyArgs): void => pushEvent({ event: 'userIdentify', ...args });

export const pushLogin = (): void => pushEvent({ event: 'login' });

export const pushCreateAccount = (): void => pushEvent({ event: 'create_account' });

// ---------- account-type choice (EWM-948) ----------

/** Which sign-up path the customer chose in the sign-in tray. */
export type AccountTypeChoice = 'cash' | 'credit';

export interface SelectAccountTypeArgs {
  account_type: AccountTypeChoice;
}

/**
 * EWM-948 AC-4 — fired the moment the customer picks a sign-up path in the
 * signed-out sign-in tray: "Create a cash account" (the in-app registration
 * drawer) or "Create a credit account" (the external applyeasy workflow).
 *
 * ONE event with an `account_type` parameter, not two event names: marketing
 * measures the split in a single GA4 report, and reporting keys off a stable
 * identifier rather than the button label (the same discipline EWM-940 asks for
 * on the neighbouring header CTA).
 *
 * Does NOT collide with `create_account` above, which fires only after a
 * COMPLETED registration (`useRegistrationForm/index.ts:168`, after
 * `registerAccount()` resolves). The two together are the intent→completion
 * funnel; neither replaces the other.
 *
 * Electus-specific by necessity: retail's previous credit/trade-application
 * event (`download_trade_application`) was REMOVED under EWM-464 as retail-only,
 * so there was no existing helper to reuse.
 *
 * ⚠ Consent-gated like every other helper here (`pushEvent` returns early when
 * `hasAnalyticsConsent()` is false), so the split it measures covers CONSENTING
 * customers only — a ratio, never a total.
 *
 * ⚠ The GTM containers (AU `GTM-KR2Z9QMH`, NZ `GTM-P6DSRFT9`) must be taught
 * this event name before it reaches GA4; this codebase only produces it.
 */
export const pushSelectAccountType = (args: SelectAccountTypeArgs): void =>
  pushEvent({ event: 'select_account_type', ...args });

// ---------- GA4 ecommerce event types (EWM-369) ----------

export interface GA4Item {
  item_id: string;
  item_name: string;
  item_brand?: string;
  /** Top-level Electus taxonomy category (see lib/analytics/ga4Items.ts). */
  item_category?: string;
  /** ex-GST for authenticated dealers; inc-GST RRP for guests; 0 when unavailable. */
  price: number;
  /** 1-based position within the list. */
  index?: number;
  item_list_id?: string;
  item_list_name?: string;
  /** Optional on all events; only meaningful for view_item. */
  in_stock?: boolean;
}

export interface ViewItemListArgs {
  item_list_id: string;
  item_list_name: string;
  items: GA4Item[];
}

export interface SelectItemArgs {
  item_list_id: string;
  item_list_name: string;
  item: GA4Item;
}

export interface ViewItemArgs {
  currency: 'AUD' | 'NZD';
  item: GA4Item;
}

// ---------- GA4 ecommerce push helpers (EWM-369) ----------
// Each helper clears `ecommerce` before the event (GA4/GTM requirement).
// Both pushes go through the same consent-gated, non-blocking pushEvent.

export const pushViewItemList = (args: ViewItemListArgs): void => {
  pushEvent({ ecommerce: null });
  pushEvent({
    event: 'view_item_list',
    ecommerce: { item_list_id: args.item_list_id, item_list_name: args.item_list_name, items: args.items },
  });
};

export const pushSelectItem = (args: SelectItemArgs): void => {
  pushEvent({ ecommerce: null });
  pushEvent({
    event: 'select_item',
    ecommerce: {
      item_list_id: args.item_list_id,
      item_list_name: args.item_list_name,
      items: [args.item],
    },
  });
};

export const pushViewItem = (args: ViewItemArgs): void => {
  pushEvent({ ecommerce: null });
  pushEvent({ event: 'view_item', ecommerce: { currency: args.currency, items: [args.item] } });
};

// ---------- ecommerce event types (EWM-370) ----------

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
}

export type DeliveryTier = 'Scheduled pick-day delivery' | 'Rush delivery';
export type PaymentType = 'Trade Account' | 'Credit Card';

export interface AddToCartArgs {
  currency: string;
  items: EcommerceItem[];
}

export interface RemoveFromCartArgs {
  currency: string;
  items: EcommerceItem[];
}

/** EWM-929 — the whole cart, emptied in one action ("Empty cart"). */
export interface CartClearedArgs {
  currency: string;
  /** The lines as they were BEFORE the clear. */
  items: EcommerceItem[];
}

export interface BeginCheckoutArgs {
  currency: string;
  items: EcommerceItem[];
}

export interface AddShippingInfoArgs {
  currency: string;
  shipping_tier: DeliveryTier;
  items: EcommerceItem[];
}

export interface SelectCheckoutOptionArgs {
  option: DeliveryTier;
}

export interface AddPaymentInfoArgs {
  currency: string;
  payment_type: PaymentType;
  items: EcommerceItem[];
}

export interface PurchaseArgs {
  transaction_id: string;
  value: number;
  tax: number;
  shipping: number;
  currency: string;
  business_unit: string;
  payment_method?: PaymentType;
  po_number?: string;
  items: EcommerceItem[];
}

// ---------- ecommerce push helpers (EWM-370) ----------

// Pushes { ecommerce: null } to clear prior ecommerce context, then the event.
// Per GA4 spec global convention: always clear before each ecommerce event.
const pushEcommerceEvent = (event: string, ecommerce: Record<string, unknown>): void => {
  pushEvent({ ecommerce: null });
  pushEvent({ event, ecommerce });
};

export const pushAddToCart = (args: AddToCartArgs): void =>
  pushEcommerceEvent('add_to_cart', { currency: args.currency, items: args.items });

export const pushRemoveFromCart = (args: RemoveFromCartArgs): void =>
  pushEcommerceEvent('remove_from_cart', { currency: args.currency, items: args.items });

// EWM-929 — AC-7. `remove_from_cart` is per-line (the cart table fires one per
// row); emptying the cart retires the whole cart in a single action, which GA4
// had no event for at all. Fired only after the BFF confirms the clear.
export const pushCartCleared = (args: CartClearedArgs): void =>
  pushEcommerceEvent('cart_cleared', { currency: args.currency, items: args.items });

export const pushBeginCheckout = (args: BeginCheckoutArgs): void =>
  pushEcommerceEvent('begin_checkout', { currency: args.currency, items: args.items });

export const pushAddShippingInfo = (args: AddShippingInfoArgs): void =>
  pushEcommerceEvent('add_shipping_info', {
    currency: args.currency,
    shipping_tier: args.shipping_tier,
    items: args.items,
  });

export const pushSelectCheckoutOption = (args: SelectCheckoutOptionArgs): void =>
  pushEcommerceEvent('select_checkout_option', { checkout_option: { option: args.option } });

export const pushAddPaymentInfo = (args: AddPaymentInfoArgs): void =>
  pushEcommerceEvent('add_payment_info', {
    currency: args.currency,
    payment_type: args.payment_type,
    items: args.items,
  });

export const pushPurchase = (args: PurchaseArgs): void => {
  const ecommerce: Record<string, unknown> = {
    transaction_id: args.transaction_id,
    value: args.value,
    tax: args.tax,
    shipping: args.shipping,
    currency: args.currency,
    business_unit: args.business_unit,
    items: args.items,
  };
  if (args.payment_method !== undefined) ecommerce.payment_method = args.payment_method;
  if (args.po_number !== undefined) ecommerce.po_number = args.po_number;
  pushEcommerceEvent('purchase', ecommerce);
};

/**
 * EWM-929 — map commercetools cart lines to the GA4 `EcommerceItem` shape.
 *
 * Lives here next to `mapLineItemsToBrazeProducts` so the two cart-line maps
 * stay side by side. The checkout client wrapper builds the same shape inline
 * (`cartEcommerceItems`) for begin_checkout / add_shipping_info; that call site
 * is left as it is — this ticket only owns the clear-cart path.
 */
export const mapLineItemsToEcommerceItems = (lineItems: LineItem[] = []): EcommerceItem[] =>
  lineItems.map((lineItem) => {
    const brand = normalizeBrandName(lineItem.variant?.attributes?.['productBrand']);
    return {
      item_id: lineItem.variant?.sku ?? lineItem.productId ?? '',
      item_name: lineItem.name ?? '',
      price: formatCentAmount(lineItem.price?.centAmount ?? 0, lineItem.price?.fractionDigits),
      quantity: lineItem.count ?? 1,
      ...(brand ? { item_brand: brand } : {}),
    };
  });

// ---------- Braze cart events (EWM-583) ----------
// Retail (jaycar/apps/web/lib/gtm) forwards two Braze-specific cart events to the
// Braze Web SDK, which is loaded *inside GTM*. We mirror the payload shape exactly
// so the retail GTM Braze tags work unchanged when the container is mirrored onto
// this storefront. Unlike the GA4 ecommerce events above, Braze events are pushed
// as a single object with nested `ecommerce` — no `ecommerce: null` clear.
// Product shape is Braze's minimal { item_id, price, quantity } (BrazeProductDTO).

export interface BrazeProduct {
  item_id: string;
  price: number;
  quantity: number;
}

export interface BrazeAddToCartArgs {
  currency: string;
  /** Full cart after the add. */
  cart_items: BrazeProduct[];
  /** The line(s) added in this action (quantity = amount added, not line total). */
  items_added: BrazeProduct[];
}

export interface BrazeRemoveFromCartArgs {
  currency: string;
  /** Full cart after the removal. */
  cart_items: BrazeProduct[];
  /** The line(s) removed in this action. */
  items_removed: BrazeProduct[];
}

/** Map commercetools cart lines to Braze's minimal product shape. */
export const mapLineItemsToBrazeProducts = (lineItems: LineItem[] = []): BrazeProduct[] =>
  lineItems.map((li) => ({
    item_id: li.variant?.sku ?? li.productId ?? '',
    price: formatCentAmount(li.price?.centAmount ?? 0, li.price?.fractionDigits),
    quantity: li.count ?? 1,
  }));

/**
 * Build the `items_added` delta. Quantity is the amount ADDED in this action (the
 * caller's `count`), not the resulting line total; price comes from the matching
 * line in the resulting cart. Mirrors retail's mapModifiedItemsToDataLayerProducts.
 */
export const buildBrazeAddedProducts = (
  cartLineItems: LineItem[] = [],
  added: Array<{ sku: string; count: number }> = [],
): BrazeProduct[] =>
  added.map(({ sku, count }) => {
    const li = cartLineItems.find((l) => l.variant?.sku === sku);
    return {
      item_id: sku,
      price: formatCentAmount(li?.price?.centAmount ?? 0, li?.price?.fractionDigits),
      quantity: count,
    };
  });

export const pushBrazeAddToCart = (args: BrazeAddToCartArgs): void =>
  pushEvent({
    event: 'braze_add_to_cart',
    ecommerce: { currency: args.currency, cart_items: args.cart_items, items_added: args.items_added },
  });

export const pushBrazeRemoveFromCart = (args: BrazeRemoveFromCartArgs): void =>
  pushEvent({
    event: 'braze_remove_from_cart',
    ecommerce: { currency: args.currency, cart_items: args.cart_items, items_removed: args.items_removed },
  });

/**
 * Hashes identity fields and fires a userIdentify event.
 * Call with `void` at call-sites (fire-and-forget) so analytics
 * never block navigation or form submission.
 */
export async function pushUserIdentifyForAccount(
  email: string,
  account:
    | {
        accountId?: string;
        firstName?: string;
        lastName?: string;
        phone?: string;
        mobile?: string;
        customerEmail?: string;
      }
    | undefined,
  countryCode: CountryCode,
): Promise<void> {
  // EWM-663 — prefer the clean customerEmail; fall back to the passed email
  // only when it's null/empty. Both the plaintext and the SHA-256 `em` derive
  // from the resolved value so GA4 + Braze never receive the mangled address.
  const resolvedEmail = account?.customerEmail || email;
  const em = await hashSHA256(resolvedEmail);
  const args: UserIdentifyArgs = { email: resolvedEmail, em, country_code: countryCode };
  if (account?.accountId) args.customer_id = account.accountId;
  if (account?.firstName) args.fn = await hashSHA256(account.firstName);
  if (account?.lastName) args.ln = await hashSHA256(account.lastName);
  const phone = account?.phone ?? account?.mobile;
  if (phone) args.ph = await hashSHA256(phone);
  pushUserIdentify(args);
}

// ---------- engagement event types (EWM-371) ----------

export interface CreateListArgs {
  /** Newly created list ID (wishlistId from the SDK response). */
  item_list_id: string;
  /** Newly created list name. */
  item_list_name: string;
}

export interface AddToListArgs {
  item_list_id: string;
  item_list_name: string;
  /** GA4 ecommerce item. Use ex-GST price for dealers, RRP for guests; 0 when unavailable. */
  item: GA4Item;
  /** Always 1 for the toggle-based UI (each add is a single unit). */
  quantity: number;
  currency: 'AUD' | 'NZD';
}

export interface ContactUsArgs {
  enquiry_type: string;
}

export interface ViewPromotionArgs {
  promotion_id?: string;
  promotion_name?: string;
  creative_name?: string;
  creative_slot?: string;
  items?: GA4Item[];
}

export type SelectPromotionArgs = ViewPromotionArgs;

// ---------- engagement push helpers (EWM-371) ----------

export const pushCreateList = (args: CreateListArgs): void => pushEvent({ event: 'create_list', ...args });

export const pushAddToList = (args: AddToListArgs): void => {
  pushEvent({ ecommerce: null });
  pushEvent({
    event: 'add_to_list',
    ecommerce: {
      currency: args.currency,
      items: [{ ...args.item, quantity: args.quantity }],
      item_list_id: args.item_list_id,
      item_list_name: args.item_list_name,
    },
  });
};

export const pushSubscribeToNewsletter = (): void => pushEvent({ event: 'subscribe_to_newsletter' });

export const pushContactUs = (args: ContactUsArgs): void => pushEvent({ event: 'contact_us', ...args });

export const pushViewPromotion = (args: ViewPromotionArgs): void => {
  pushEvent({ ecommerce: null });
  pushEvent({ event: 'view_promotion', ecommerce: args });
};

export const pushSelectPromotion = (args: SelectPromotionArgs): void => {
  pushEvent({ ecommerce: null });
  pushEvent({ event: 'select_promotion', ecommerce: args });
};

export interface CheckoutBlockedArgs {
  /** Why the click was refused — the `SubmitErrorKey`, e.g. `cart-modified`. */
  reason: string;
  cart_id?: string;
}

// EWM-714 follow-up — a Complete Order click that is REFUSED leaves no trace,
// which matters more here than usual: a false positive on the stale-cart gate
// blocks a legitimate order, and that is precisely what forced the #929 revert.
// Without a signal, a production spike would only surface as support tickets.
//
// ⚠ Consent-gated, like every other event here (`pushEvent` returns early when
// `hasAnalyticsConsent()` is false), so this counts refusals for CONSENTING
// customers only — treat it as a trend, never as a total. There is no
// browser-side error collection in this app, so the paired `console.warn` at the
// call site is complete but only visible in a live session. Closing that gap
// properly needs a server-side signal or error tracking; neither exists yet.
export const pushCheckoutBlocked = (args: CheckoutBlockedArgs): void =>
  pushEvent({ event: 'checkout_blocked', ...args });

export interface UploadRejectedArgs {
  /** Data rows the file yielded, header excluded. 0 = nothing parseable at all. */
  total_rows: number;
  /** Rows not accepted. Equal to `total_rows`, since the event only fires when nothing was. */
  rejected_rows: number;
  /** Delimiter the parser detected: `comma` | `semicolon` | `tab` | `pipe` | `none`. */
  delimiter: string;
  /** Machine reason code → count, e.g. `{ 'missing-quantity': 4 }`. Never file content. */
  reasons: Record<string, number>;
}

// EWM-949 — an order-upload that yields ZERO usable lines is invisible today.
// The parse is entirely client-side, so no BFF log, no server metric and no
// error tracker ever sees it; the customer believes the order went in, and the
// only signal the business gets is a support call weeks later (which is exactly
// how this ticket was raised). AC 9 asks for that signal; this is it.
//
// ⚠ Consent-gated, like every other event here (`pushEvent` returns early when
// `hasAnalyticsConsent()` is false), so this counts rejected uploads for
// CONSENTING customers only — treat it as a trend, never as a total. Same
// caveat as `pushCheckoutBlocked` above, and for the same reason: there is no
// browser-side error collection in this app, so the paired `console.warn` at
// the call site covers everyone but is only visible in a live session.
//
// Deliberately carries NO file name, NO file contents and NO SKU — only counts,
// the detected delimiter and machine reason codes.
export const pushUploadRejected = (args: UploadRejectedArgs): void => pushEvent({ event: 'upload_rejected', ...args });
